import React, { useState, useEffect, useMemo, useRef } from "react";
import PayWidgetEvents from "../../../components/PayWidgetEvents";
import {
  bundleSuccessMessage,
  chargedButIncompleteMessage,
  readBundleChargedButIncomplete,
} from "../../../utils/bundlePurchase";
import { useAxiosInstance } from "../../../services/Interceptor";
import {
  GetAdminPrice,
  GetEventCombinePrice,
  SaveAppliedCouponCode,
} from "../../../services/EventsService";
import { useSelector, useDispatch } from "react-redux";
import {
  setSubscriptionDetails,
  registerAccountStore,
} from "../../../store/slices/accountSlice";
import { getCouponPrice } from "../../../services/PublicServices";
import {
  registerAthlete,
  registerAthleteWithEventId,
} from "../../../services/AthleteService";
import { registerCoach } from "../../../services/CoachService";
import {
  PostPayment,
  getCombineBundleOffer,
  purchaseCombineBundle,
} from "../../../services/PaymentService";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { useTrackEvent } from "../../../hooks/useTrackEvent";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSpinner } from "@fortawesome/free-solid-svg-icons";

// Bundle choice persisted by the bundle offer screen (skips AthleteMembershipPlans).
// Only honored when it covers the combine being registered for, so a key left
// behind by an abandoned attempt can't take over an unrelated checkout.
const readBundleChoice = (eventId) => {
  try {
    const saved = JSON.parse(localStorage.getItem("bundleDetails") || "null");
    const coversEvent = saved?.combines?.some(
      (combine) => Number(combine.eventId) === Number(eventId),
    );
    return saved?.bundleId && eventId && coversEvent ? saved : null;
  } catch {
    return null;
  }
};

function PaymentOption() {
  const apiInstance = useAxiosInstance();
  const [isLoading, setIsLoading] = useState(true);
  const [subscriptionType, setSubscriptionType] = useState("monthly");
  const [showPayWidget, setShowPayWidget] = useState(false);
  const [spotRegPricing, setSpotRegPricing] = useState({
    combinePrice: 0,
    annualPrice: 0,
    combinePosDrillPrice: 0,
    monthlyPrice: 0,
  });
  const [adminPricing, setAdminPricing] = useState({
    monthlyPrice: 0,
    annualPrice: 0,
    combinePrice: 0,
  });
  const [couponCode, setCouponCode] = useState("");
  const getDefaultDiscountState = () => ({
    amount: 0,
    baseAmount: 0,
    percentage: 0,
    discountType: "",
    couponType: "",
  });

  const [discountInfo, setDiscountInfo] = useState(() => {
    const spotOpts = JSON.parse(
      localStorage.getItem("subscriptionDetails") || "{}",
    );
    const isActuallySpot =
      spotOpts?.combine === true ||
      spotOpts?.positionDrill === true ||
      spotOpts?.subscription === true;
    if (isActuallySpot) {
      const stored = localStorage.getItem("appliedCouponDiscount");
      return stored ? JSON.parse(stored) : getDefaultDiscountState();
    }
    return getDefaultDiscountState();
  });
  const [couponError, setCouponError] = useState("");
  const [isRegisteringFree, setIsRegisteringFree] = useState(false);
  const [isIntellipayReady, setIsIntellipayReady] = useState(false);

  const dispatch = useDispatch();
  const spotRegPayOptions = useMemo(() => {
    return JSON.parse(localStorage.getItem("subscriptionDetails") || "{}");
  }, []);
  const storedData = JSON.parse(
    localStorage.getItem("registeredAccount") || "{}",
  );
  const accounts = useSelector((state) => state.account.register);
  const user = accounts.length ? accounts[0] : storedData;
  const bundleChoice = useMemo(
    () => readBundleChoice(user.eventId),
    [user.eventId],
  );
  const hasBundle = Boolean(bundleChoice);
  // The accepted bundle as the server prices it now. localStorage only says
  // which bundle was accepted; its price is never trusted for the charge.
  const [verifiedBundle, setVerifiedBundle] = useState(null);
  // Card already charged for this bundle but registration incomplete: never
  // offer it for payment again. Only applies to a bundle checkout.
  const [chargedButIncomplete, setChargedButIncomplete] = useState(() =>
    hasBundle ? readBundleChargedButIncomplete() : null,
  );
  const payButtonRef = useRef(null);
  const bundlePaymentOpenedRef = useRef(false);
  const navigate = useNavigate();
  const [payWidgetVersion, setPayWidgetVersion] = useState(0);
  const trackEvent = useTrackEvent();

  useEffect(() => {
    if (!accounts.length && storedData) {
      dispatch(registerAccountStore(storedData));
    }
  }, [dispatch, accounts.length, storedData]);

  useEffect(() => {
    const fetchPrices = async () => {
      let userPricing = "";
      let combineData = "";
      try {
        setIsLoading(true);
        if (hasBundle) {
          // Bundle purchases use the bundle price from the offer route, not
          // GetEventCombinePrice — nothing here applies to that amount.
          const offer = await getCombineBundleOffer(apiInstance, user.eventId);
          if (
            !offer.success ||
            offer.data?.combineBundleId !== bundleChoice.bundleId
          ) {
            toast.error(
              "This bundle is no longer available. Please choose your registration again.",
              { position: "top-right" },
            );
            navigate("/athlete-membership");
            return;
          }
          setVerifiedBundle({
            bundleId: offer.data.combineBundleId,
            bundlePrice: Number(offer.data.bundlePrice ?? 0),
            combines: offer.data.combines ?? [],
          });
          setShowPayWidget(true);
          return;
        }
        if (user.eventId) {
          combineData = await GetEventCombinePrice(apiInstance, user.eventId);
        }

        const adminPrices = await GetAdminPrice(apiInstance);

        const athletePricing = adminPrices?.data?.find(
          (option) => option.role.toLowerCase() === "athlete",
        );

        if (spotRegPayOptions && Object.keys(spotRegPayOptions).length > 0) {
          setSpotRegPricing({
            combinePrice: combineData?.data?.combinePrice ?? 0,
            annualPrice: athletePricing?.pricingOptions?.annualPrice ?? 0,
            combinePosDrillPrice: combineData?.data?.positionDrillPrice ?? 0,
            monthlyPrice: athletePricing?.pricingOptions?.monthlyPrice ?? 0,
          });
          trackEvent("Registration3_ViewedPricing", {
            event_id: user.eventId,
            combine_price: combineData?.data?.combinePrice ?? 0,
          });
          setShowPayWidget(true);
        } else {
          userPricing = adminPrices.data.find(
            (option) => option.role.toLowerCase() === user.role.toLowerCase(),
          );
        }

        if (userPricing) {
          setAdminPricing(userPricing.pricingOptions);
          setShowPayWidget(true);
        }
      } catch (error) {
        console.error("Error fetching pricing:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchPrices();
  }, [
    apiInstance,
    bundleChoice,
    hasBundle,
    navigate,
    spotRegPayOptions,
    user.eventId,
    user.role,
    trackEvent,
  ]);

  // Replaces the single-event price calculation entirely: a bundle spans
  // multiple combines, so none of GetEventCombinePrice's terms apply.
  const basePrice = hasBundle
    ? Number(verifiedBundle?.bundlePrice ?? 0)
    : spotRegPayOptions && Object.keys(spotRegPayOptions).length > 0
      ? (spotRegPayOptions.combine ? spotRegPricing.combinePrice : 0) +
        (spotRegPayOptions.positionDrill
          ? spotRegPricing.combinePosDrillPrice
          : 0) +
        (spotRegPayOptions.subscription ? spotRegPricing.annualPrice : 0)
      : subscriptionType === "monthly"
        ? adminPricing.monthlyPrice
        : adminPricing.annualPrice;

  // Bundles never carry a coupon; a stale appliedCouponDiscount left over from
  // an abandoned earlier attempt must not reduce the bundle price.
  const discountAmount = hasBundle ? 0 : Number(discountInfo.amount || 0);

  const selectedPrice = Math.max(basePrice - discountAmount, 0);

  const handleToggle = (type) => {
    setSubscriptionType(type);
    setShowPayWidget(true);
  };

  const finalSubscriptionType = useMemo(() => {
    if (hasBundle) return "bundle";
    if (spotRegPayOptions && Object.keys(spotRegPayOptions).length > 0) {
      const result = [];
      if (spotRegPayOptions.combine) result.push("combine");
      if (spotRegPayOptions.positionDrill) result.push("positionDrill");
      if (spotRegPayOptions.subscription) result.push("subscription");
      return result;
    } else {
      return subscriptionType;
    }
  }, [hasBundle, spotRegPayOptions, subscriptionType]);

  const staticUser = useMemo(
    () => ({
      email: user?.email,
      id: user.id,
      price: selectedPrice,
      eventId: user.eventId,
    }),
    [user?.email, user.id, selectedPrice, user.eventId],
  );

  useEffect(() => {
    if (staticUser) {
      dispatch(
        setSubscriptionDetails({
          subscriptionType: finalSubscriptionType,
          subscriptionPrice: staticUser.price,
        }),
      );
    }
  }, [dispatch, finalSubscriptionType, staticUser]);

  const isSpot = spotRegPayOptions && Object.keys(spotRegPayOptions).length > 0;
  const combineSelected = Boolean(spotRegPayOptions?.combine);
  const positionSelected = Boolean(spotRegPayOptions?.positionDrill);
  const subscriptionSelected = Boolean(spotRegPayOptions?.subscription);

  const combinePrice = combineSelected
    ? Number(spotRegPricing.combinePrice ?? 0)
    : 0;
  const positionDrillPrice = positionSelected
    ? Number(spotRegPricing.combinePosDrillPrice ?? 0)
    : 0;

  const spotSubscriptionPrice = subscriptionSelected
    ? Number(spotRegPricing.annualPrice ?? 0)
    : 0;

  const normalSubscriptionPrice =
    subscriptionType === "monthly"
      ? Number(adminPricing.monthlyPrice ?? 0)
      : Number(adminPricing.annualPrice ?? 0);

  const couponNotApplicableMessage = (couponType) => {
    switch (couponType) {
      case "Combine":
        return "This coupon applies to Combine only. Please select a Combine Pass to use it.";

      case "PositionDrills":
        return "This coupon applies to Position Drills only. Please select Position Drills to use it.";

      case "CombineAndPositionDrills":
        return "This coupon applies to Combine and/or Position Drills. Please select at least one to use it.";

      case "Subscription":
        return "This coupon applies to Subscription only (Monthly or Annual). Please choose a subscription plan to use it.";

      default:
        return "This coupon is not applicable to your current selection.";
    }
  };

  const validateCouponApplicability = (couponType) => {
    if (!isSpot) {
      if (couponType !== "Subscription") {
        return {
          ok: false,
          eligibleAmount: 0,
          message:
            "This coupon can only be applied during Combine/Position-Drills checkout.",
        };
      }

      const eligible = normalSubscriptionPrice;
      if (!eligible || eligible <= 0) {
        return {
          ok: false,
          eligibleAmount: 0,
          message: couponNotApplicableMessage("Subscription"),
        };
      }
      return { ok: true, eligibleAmount: eligible };
    }

    if (couponType === "Combine") {
      if (!combineSelected || combinePrice <= 0) {
        return {
          ok: false,
          eligibleAmount: 0,
          message: couponNotApplicableMessage("Combine"),
        };
      }
      return { ok: true, eligibleAmount: combinePrice };
    }

    if (couponType === "PositionDrills") {
      if (!positionSelected || positionDrillPrice <= 0) {
        return {
          ok: false,
          eligibleAmount: 0,
          message: couponNotApplicableMessage("PositionDrills"),
        };
      }
      return { ok: true, eligibleAmount: positionDrillPrice };
    }

    if (couponType === "CombineAndPositionDrills") {
      const eligibleAmount = combinePrice + positionDrillPrice;
      if (eligibleAmount <= 0) {
        return {
          ok: false,
          eligibleAmount: 0,
          message: couponNotApplicableMessage("CombineAndPositionDrills"),
        };
      }
      return { ok: true, eligibleAmount };
    }

    if (couponType === "Subscription") {
      if (!subscriptionSelected || spotSubscriptionPrice <= 0) {
        return {
          ok: false,
          eligibleAmount: 0,
          message: couponNotApplicableMessage("Subscription"),
        };
      }
      return { ok: true, eligibleAmount: spotSubscriptionPrice };
    }

    return {
      ok: false,
      eligibleAmount: 0,
      message: couponNotApplicableMessage(couponType),
    };
  };

  const applyCouponCode = async () => {
    try {
      const response = await getCouponPrice(apiInstance, couponCode);

      const amount = Number(response?.amount ?? 0);
      const couponType = String(
        response?.type || response?.couponType || "",
      ).trim();
      const discountType = String(response?.discountType || "").trim();

      if (!amount || amount <= 0) {
        setCouponError("The coupon code is invalid or does not apply.");
        setDiscountInfo(getDefaultDiscountState());
        return;
      }

      if (!couponType) {
        setCouponError("Coupon type is missing. Please contact support.");
        setDiscountInfo(getDefaultDiscountState());
        return;
      }

      const { ok, eligibleAmount, message } =
        validateCouponApplicability(couponType);

      if (!ok) {
        setCouponError(
          message || "This coupon is not applicable to your current selection.",
        );
        setDiscountInfo(getDefaultDiscountState());
        return;
      }

      let appliedBaseAmount = eligibleAmount;
      let appliedDiscountAmount = 0;
      let appliedPercentage = 0;

      if (discountType === "Percent Off") {
        appliedPercentage = amount;
        appliedDiscountAmount = Math.min(
          (appliedBaseAmount * amount) / 100,
          appliedBaseAmount,
        );
      } else {
        appliedDiscountAmount = Math.min(amount, appliedBaseAmount);
      }

      const nextDiscountInfo = {
        amount: appliedDiscountAmount,
        baseAmount: appliedBaseAmount,
        percentage: appliedPercentage,
        discountType,
        couponType,
      };

      const isSameDiscount =
        Number(discountInfo.amount) === Number(nextDiscountInfo.amount) &&
        Number(discountInfo.baseAmount) ===
          Number(nextDiscountInfo.baseAmount) &&
        Number(discountInfo.percentage) ===
          Number(nextDiscountInfo.percentage) &&
        discountInfo.discountType === nextDiscountInfo.discountType &&
        discountInfo.couponType === nextDiscountInfo.couponType;

      if (isSameDiscount) {
        setCouponError("");
        localStorage.setItem("appliedCouponCode", couponCode);
        localStorage.setItem("appliedCouponType", couponType);
        return;
      }

      setDiscountInfo(nextDiscountInfo);
      setCouponError("");

      trackEvent("Registration3_5_AppliedCoupon", {
        event_id: user.eventId,
        coupon_code: couponCode,
        coupon_type: couponType,
        discount_amount: nextDiscountInfo.amount,
      });

      setIsIntellipayReady(false);
      setShowPayWidget(true);
      setPayWidgetVersion((v) => v + 1);

      localStorage.setItem("appliedCouponCode", couponCode);
      localStorage.setItem("appliedCouponType", couponType);
    } catch (error) {
      console.error("Invalid coupon code", error);
      setCouponError("Invalid coupon code");
      setDiscountInfo(getDefaultDiscountState());
    }
  };

  useEffect(() => {
    // Spot-reg coupon invalidation is handled in AthleteMembershipPlans before navigation.
    if (isSpot) return;

    if (discountInfo.amount > 0) {
      const couponType = localStorage.getItem("appliedCouponType") || "";
      if (couponType) {
        const { ok } = validateCouponApplicability(couponType);
        if (!ok) {
          setDiscountInfo(getDefaultDiscountState());
          setCouponError(
            "Selection changed — coupon removed because it no longer applies.",
          );
          localStorage.removeItem("appliedCouponCode");
          localStorage.removeItem("appliedCouponType");
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isSpot,
    spotRegPayOptions,
    subscriptionType,
    spotRegPricing,
    adminPricing,
    discountInfo.amount,
  ]);

  const postAppliedCouponCode = async (code) => {
    await SaveAppliedCouponCode(apiInstance, code);
    localStorage.removeItem("appliedCouponCode");
    localStorage.removeItem("appliedCouponType");
    localStorage.removeItem("appliedCouponDiscount");
  };

  const handleRegister = async () => {
    const currentUser = user;
    let newUserId = currentUser.id;
    setIsRegisteringFree(true);

    try {
      if (hasBundle) {
        // Zero-total bundle path: register and post the bundle purchase with
        // a zero amount and no IntelliPay token, without opening the widget.
        // Position drills are granted by the combine-bundle route, so no
        // separate position drill payment is sent.
        if (!verifiedBundle) {
          throw new Error("This bundle is no longer available.");
        }
        const reg = await registerAthleteWithEventId(
          apiInstance,
          currentUser,
          currentUser.eventId,
        );
        if (reg.status !== 200) {
          throw new Error("Spot combine registration failed.");
        }
        newUserId = reg.data.id;

        const bundlePaid = await purchaseCombineBundle(apiInstance, {
          userId: newUserId,
          combineBundleId: verifiedBundle.bundleId,
          eventId: currentUser.eventId,
          amount: 0,
          transactionId: "",
          intellipayToken: "",
        });
        if (bundlePaid.status !== 200) {
          throw new Error("Bundle payment failed.");
        }

        trackEvent("Registration5_RegistrationCompleted", {
          event_id: currentUser.eventId,
          payment_method: "free",
          bundle_id: verifiedBundle.bundleId,
        });
        localStorage.removeItem("appliedCouponCode");
        localStorage.removeItem("appliedCouponType");
        localStorage.removeItem("appliedCouponDiscount");
        localStorage.removeItem("bundleDetails");
        toast.success(bundleSuccessMessage(verifiedBundle), {
          position: "top-right",
        });
        navigate("/register/registrationsuccess");
        return;
      }

      if (!currentUser.registered) {
        if (currentUser.role === "athlete") {
          const reg = await registerAthlete(apiInstance, "0", currentUser);
          if (reg.status === 200) newUserId = reg.data.id;
          else throw new Error("Athlete registration failed.");
        } else if (currentUser.role === "university") {
          const reg = await registerCoach(apiInstance, currentUser);
          if (reg.status === 200) newUserId = reg.data.id;
          else throw new Error("Coach registration failed.");
        } else if (currentUser.role === "spotcombine") {
          const reg = await registerAthleteWithEventId(
            apiInstance,
            currentUser,
            currentUser.eventId,
          );
          if (reg.status === 200) newUserId = reg.data.id;
          else throw new Error("Spot combine registration failed.");
        }
      }
      let paymentPayloads = [];

      if (spotRegPayOptions?.combine) {
        paymentPayloads.push({
          userId: newUserId,
          amount: 0,
          isCombine: true,
          isPositionDrill: false,
          isAnnualSubscription: false,
          isMonthlySubscription: false,
          isCash: false,
          eventId: currentUser.eventId,
          transactionId: "",
          intellipayToken: "",
        });
      }

      if (spotRegPayOptions?.positionDrill) {
        paymentPayloads.push({
          userId: newUserId,
          amount: 0,
          isCombine: false,
          isPositionDrill: true,
          isAnnualSubscription: false,
          isMonthlySubscription: false,
          isCash: false,
          eventId: currentUser.eventId,
          transactionId: "",
          intellipayToken: "",
        });
      }

      if (spotRegPayOptions?.subscription) {
        paymentPayloads.push({
          userId: newUserId,
          amount: 0,
          isCombine: false,
          isPositionDrill: false,
          isAnnualSubscription: true,
          isMonthlySubscription: false,
          isCash: false,
          eventId: 0,
          transactionId: "",
          intellipayToken: "",
        });
      }

      if (!spotRegPayOptions || Object.keys(spotRegPayOptions).length === 0) {
        paymentPayloads.push({
          userId: newUserId,
          amount: 0,
          isCombine: false,
          isPositionDrill: false,
          isAnnualSubscription: subscriptionType === "annual",
          isMonthlySubscription: subscriptionType === "monthly",
          isCash: false,
          eventId: 0,
          transactionId: "",
          intellipayToken: "",
        });
      }

      for (const payload of paymentPayloads) {
        const paid = await PostPayment(apiInstance, payload);
        if (paid.status !== 200) {
          throw new Error("Payment failed.");
        }
      }

      const couponCode = localStorage.getItem("appliedCouponCode");
      if (couponCode) {
        await postAppliedCouponCode(couponCode);
      }

      trackEvent("Registration5_RegistrationCompleted", {
        event_id: user.eventId,
        payment_method: "free",
      });
      toast.success("Registration and payment successful!", {
        position: "top-right",
      });
      navigate("/register/registrationsuccess");
    } catch (error) {
      trackEvent("Registration4_5_PaymentFailed", {
        event_id: user.eventId,
        reason: error.message ?? "unknown",
      });
      toast.error(error.message || "Registration or payment failed", {
        position: "top-right",
      });
    } finally {
      setIsRegisteringFree(false);
    }
  };

  // A bundle has no coupon or plan choices to make here, so open the IntelliPay
  // modal as soon as it is ready. Clicking the Pay button keeps the widget's own
  // submit handling; the button stays on the page if the athlete closes it.
  useEffect(() => {
    if (!hasBundle || !isIntellipayReady || bundlePaymentOpenedRef.current) {
      return;
    }
    bundlePaymentOpenedRef.current = true;
    payButtonRef.current?.click();
  }, [hasBundle, isIntellipayReady]);

  useEffect(() => {
    if (selectedPrice === 0) {
      setShowPayWidget(false);
      setIsIntellipayReady(false);
    } else {
      setShowPayWidget(true);
    }
  }, [selectedPrice]);

  return (
    <>
      <div className="flex items-center justify-center mt-6">
        <div className="w-full max-w-4xl px-3">
          <div className="text-center text-white uppercase text-2xl xl:text-3xl lg:text-3xl font-black italic">
            Simple, Transparent Pricing
          </div>
          <p className="text-center text-white text-base font-normal py-2">
            Clear, straightforward pricing with no hidden fees.
          </p>

          {isLoading ? (
            <div className="flex justify-center items-center py-20">
              <FontAwesomeIcon
                icon={faSpinner}
                className="animate-spin text-4xl text-white"
              />
            </div>
          ) : (
            <>
              <div className="mt-6 grid justify-items-center">
                {!hasBundle &&
                  (!spotRegPayOptions ||
                    Object.keys(spotRegPayOptions).length === 0) && (
                    <div className="price-top flex gap-4">
                      <button
                        onClick={() => handleToggle("monthly")}
                        className={`text-white px-16 py-4 rounded-full block uppercase cursor-pointer transition duration-300 ${subscriptionType === "monthly" ? "bg-red-500" : "bg-gray-600"}`}
                      >
                        Monthly
                      </button>

                      <button
                        onClick={() => handleToggle("annual")}
                        className={`text-white px-16 py-4 rounded-full block uppercase cursor-pointer transition duration-300 ${subscriptionType === "annual" ? "bg-red-500" : "bg-gray-600"}`}
                      >
                        Annual
                      </button>
                    </div>
                  )}

                <div className="w-96 xl:w-96 lg:w-96 m-auto mt-6">
                  <div className="rounded-2xl border border-white-600">
                    <div className="flex flex-col px-6 py-4 text-white space-y-3">
                      {hasBundle ? (
                        <>
                          {verifiedBundle?.combines.map((combine) => (
                            <div
                              className="flex justify-between"
                              key={combine.eventId}
                            >
                              <span>{combine.name}</span>
                            </div>
                          ))}
                          <div className="flex justify-between">
                            <span>Bundle Price</span>
                            <span>${basePrice.toFixed(2)}</span>
                          </div>
                        </>
                      ) : spotRegPayOptions &&
                        Object.keys(spotRegPayOptions).length > 0 ? (
                        <>
                          {spotRegPayOptions.combine && (
                            <div className="flex justify-between">
                              <span>Combine Pass</span>
                              <span>
                                ${(spotRegPricing.combinePrice ?? 0).toFixed(2)}
                              </span>
                            </div>
                          )}
                          {spotRegPayOptions.positionDrill && (
                            <div className="flex justify-between">
                              <span>Position Drills</span>
                              <span>
                                $
                                {(
                                  spotRegPricing.combinePosDrillPrice ?? 0
                                ).toFixed(2)}
                              </span>
                            </div>
                          )}
                          {spotRegPayOptions.subscription && (
                            <div className="flex justify-between">
                              <span>Annual Subscription</span>
                              <span>
                                ${(spotRegPricing.annualPrice ?? 0).toFixed(2)}
                              </span>
                            </div>
                          )}
                        </>
                      ) : (
                        <>
                          <div className="flex justify-between">
                            <span>
                              {subscriptionType === "monthly"
                                ? "Monthly Subscription"
                                : "Annual Subscription"}
                            </span>
                            <span>
                              $
                              {(subscriptionType === "monthly"
                                ? (adminPricing.monthlyPrice ?? 0)
                                : (adminPricing.annualPrice ?? 0)
                              ).toFixed(2)}
                            </span>
                          </div>
                        </>
                      )}

                      {discountAmount > 0 && (
                        <div className="flex justify-between text-green-400 font-medium text-sm">
                          <span>Discount Amount</span>
                          <span>- ${discountAmount.toFixed(2)}</span>
                        </div>
                      )}
                      <div className="border-t border-white-600 my-2"></div>
                      <div className="flex justify-between font-bold text-lg">
                        <span>Total:</span>
                        <span>${(selectedPrice ?? 0).toFixed(2)}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="grid justify-items-center">
                {!isSpot && !hasBundle && (
                  <div className="w-92 xl:w-96 lg:w-96 m-auto mt-4 px-6 text-white">
                    <div className="flex items-center space-x-2">
                      <label htmlFor="coupon" className="text-sm font-semibold">
                        Coupon Code:
                      </label>
                      <input
                        type="text"
                        id="coupon"
                        value={couponCode}
                        onChange={(e) => setCouponCode(e.target.value)}
                        className="text-black px-2 py-1 rounded"
                      />
                      <button
                        onClick={applyCouponCode}
                        className="bg-white text-black px-3 py-1 rounded hover:bg-gray-200"
                      >
                        Apply
                      </button>
                    </div>
                    {couponError && (
                      <p className="text-red-400 mt-2 text-sm">{couponError}</p>
                    )}
                  </div>
                )}

                <div className="grid justify-items-center">
                  {selectedPrice === 0 ? (
                    <div
                      role="button"
                      tabIndex={0}
                      onClick={handleRegister}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          handleRegister();
                        }
                      }}
                      className="bg-green-500 mt-4 text-white uppercase rounded px-12 py-2 hover:bg-green-700 cursor-pointer text-center"
                    >
                      Register
                    </div>
                  ) : chargedButIncomplete ? (
                    <>
                      <p
                        role="alert"
                        className="mt-4 max-w-md text-center text-red-400 text-sm"
                      >
                        {chargedButIncompleteMessage(
                          chargedButIncomplete.transactionId,
                        )}
                      </p>
                      <button
                        type="button"
                        disabled
                        className="mt-4 text-white uppercase rounded px-12 py-2 bg-gray-500 cursor-not-allowed opacity-70"
                      >
                        Payment received – contact support
                      </button>
                    </>
                  ) : (
                    <button
                      ref={payButtonRef}
                      type="button"
                      className={`mt-4 text-white uppercase rounded px-12 py-2 ipayfield
                        ${isIntellipayReady ? "bg-red-500 hover:bg-red-700 cursor-pointer" : "bg-gray-500 cursor-not-allowed opacity-70"}`}
                      data-ipayname="submit"
                      disabled={!isIntellipayReady}
                      aria-busy={!isIntellipayReady}
                      onClick={(e) => {
                        if (!isIntellipayReady) {
                          e.preventDefault();
                          toast.info("IntelliPay is loading… please wait.", {
                            position: "top-right",
                          });
                          return;
                        }
                      }}
                    >
                      {isIntellipayReady ? "Pay" : "Loading…"}
                    </button>
                  )}
                </div>
              </div>
            </>
          )}
        </div>

        {showPayWidget &&
          selectedPrice > 0 &&
          !isRegisteringFree &&
          !chargedButIncomplete && (
            <PayWidgetEvents
              key={`paywidget-${payWidgetVersion}-${selectedPrice}`}
              userEmail={staticUser.email}
              userId={staticUser.id}
              paymentAmount={staticUser.price}
              eventId={staticUser.eventId}
              subscriptionType={finalSubscriptionType}
              bundleChoice={verifiedBundle}
              onIntellipayReady={setIsIntellipayReady}
              onChargedButIncomplete={(transactionId) =>
                setChargedButIncomplete({ transactionId })
              }
            />
          )}
      </div>
    </>
  );
}

export default PaymentOption;
